import React, {FC, useEffect, useState} from "react";
import {useLocation, useParams} from "react-router-dom";
import {Helmet} from "react-helmet-async";
import {MapPinIcon} from "@heroicons/react/24/outline";
import {useAuth} from "contexts/AuthContext";
import {EventType, OrganizerType} from "data/types";
import {
  useGetEventsByOrganizerQuery,
  useGetFollowersCountQuery,
  useGetFollowsQuery,
} from "store/api";
import Avatar from "shared/Avatar/Avatar";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import {FollowButton} from "components/Organizer/FollowButton";
import OrganizerEventCard from "components/Organizer/OrganizerEventCard";

const PAGE_SIZE = 12;

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/**
 * An organizer and its upcoming events. There is no endpoint for the
 * organizer itself: the event it was opened from passes it whole; opened
 * from a link, its name and picture come from its events or the follow.
 */
const OrganizerPage: FC = () => {
  const {id = ""} = useParams();
  const location = useLocation();
  const {isLoggedIn} = useAuth();

  const [page, setPage] = useState(1);
  const [events, setEvents] = useState<EventType[]>([]);

  const {data, isFetching} = useGetEventsByOrganizerQuery(
    {organizer: id, page, limit: PAGE_SIZE},
    {skip: !id},
  );
  const {data: countData} = useGetFollowersCountQuery(id, {
    skip: !id || !isLoggedIn,
  });
  const {data: followData} = useGetFollowsQuery(
    {target: id},
    {skip: !id || !isLoggedIn},
  );

  useEffect(() => {
    setPage(1);
    setEvents([]);
  }, [id]);

  useEffect(() => {
    const pageEvents: EventType[] = data?.eventsDB ?? [];
    setEvents((prev) =>
      page === 1
        ? pageEvents
        : [
            ...prev,
            ...pageEvents.filter((e) => !prev.some((p) => p._id === e._id)),
          ],
    );
  }, [data]);

  const fromState = (location.state as {organizer?: OrganizerType} | null)
    ?.organizer;
  const organizer: OrganizerType | undefined =
    (fromState?._id === id ? fromState : undefined) ??
    events[0]?.organizer ??
    followData?.data?.[0]?.target;
  const name = organizer?.businessName || "Organizador";
  const followers: number | undefined = countData?.data?.total;
  const totalEvents: number | undefined = data?.total;
  const hasMore = page < (data?.totalPages ?? 0);
  const isFirstLoad = isFetching && events.length === 0;

  const renderHeader = () => (
    <section className="overflow-hidden rounded-3xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
      <div className="relative h-28 sm:h-44 overflow-hidden bg-gradient-to-r from-primary-500 to-primary-200">
        {organizer?.img && (
          <img
            src={organizer.img}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-125 object-cover opacity-70 blur-2xl"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20" />
      </div>

      <div className="px-5 pb-6 sm:px-8 sm:pb-8">
        <div className="-mt-12 sm:-mt-16 flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-6">
          <Avatar
            sizeClass="w-24 h-24 sm:w-32 sm:h-32 text-3xl"
            containerClassName="ring-4 ring-white dark:ring-neutral-900"
            imgUrl={organizer?.img}
            userName={name}
          />
          <div className="min-w-0 flex-1 sm:pb-1">
            <p className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Organizador
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-semibold break-words">
              {name}
            </h1>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-500 dark:text-neutral-400">
              {followers !== undefined && (
                <span className="tabular-nums">
                  {plural(followers, "seguidor", "seguidores")}
                </span>
              )}
              {totalEvents !== undefined && (
                <span className="tabular-nums">
                  {plural(totalEvents, "evento próximo", "eventos próximos")}
                </span>
              )}
              {organizer?.address && (
                <span className="flex items-center gap-1">
                  <MapPinIcon className="w-4 h-4" />
                  {organizer.address}
                </span>
              )}
            </div>
          </div>
          <div className="sm:pb-1">
            <FollowButton organizerId={id} />
          </div>
        </div>

        {organizer?.description && (
          <p className="mt-6 max-w-3xl whitespace-pre-line text-neutral-600 dark:text-neutral-300">
            {organizer.description}
          </p>
        )}
      </div>
    </section>
  );

  const renderEvents = () => (
    <section className="mt-12 space-y-6">
      <h2 className="text-2xl font-semibold">Próximos eventos</h2>

      {isFirstLoad ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="animate-pulse overflow-hidden rounded-3xl border border-neutral-100 dark:border-neutral-800"
            >
              <div className="aspect-square bg-neutral-200 dark:bg-neutral-800" />
              <div className="space-y-3 p-4">
                <div className="h-4 w-3/4 rounded bg-neutral-200 dark:bg-neutral-800" />
                <div className="h-3 w-1/2 rounded bg-neutral-200 dark:bg-neutral-800" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-neutral-300 dark:border-neutral-700 px-6 py-14 text-center">
          <p className="font-medium">
            Este organizador no tiene eventos próximos
          </p>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {isLoggedIn
              ? "Síguelo para enterarte cuando publique uno nuevo."
              : "Vuelve pronto para ver sus nuevos eventos."}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {events.map((event) => (
              <OrganizerEventCard key={event._id} event={event} />
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center">
              <ButtonSecondary
                loading={isFetching}
                disabled={isFetching}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Ver más eventos
              </ButtonSecondary>
            </div>
          )}
        </>
      )}
    </section>
  );

  return (
    <div className="nc-OrganizerPage">
      <Helmet>
        <title>Bee || {name}</title>
      </Helmet>
      <div className="container mt-8 sm:mt-12 mb-24 lg:mb-32">
        {renderHeader()}
        {renderEvents()}
      </div>
    </div>
  );
};

export default OrganizerPage;
